// pages/TemplateDetails.js
import React, { useState, useEffect } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import Layout from "../components/common/Layout";
import templates from "../Data/templatesData";
import TemplatePreviewWrapper from "../components/templates/TemplatePreviewWrapper";

const TemplateDetails = () => {
  const { slug } = useParams();
  const location = useLocation();
  const history = useHistory();

  // Default to the first template if no slug is provided
  const [selectedTemplate, setSelectedTemplate] = useState(
    slug || templates[0]?.slug
  );

  const template = templates.find((t) => t.slug === selectedTemplate);

  // Determine if the current route is for full view
  const isFullView = location.pathname.endsWith("/fullview");

  // Load saved customization or defaults
  const [brandName, setBrandName] = useState(
    () => localStorage.getItem("brandName") || "Your Brand"
  );
  const [tagline, setTagline] = useState(
    () => localStorage.getItem("tagline") || "Your Tagline"
  );
  const [colorTheme, setColorTheme] = useState(
    () => localStorage.getItem("colorTheme") || "blue"
  );
  const [logo, setLogo] = useState(() => localStorage.getItem("logo") || null);

  useEffect(() => {
    // Save customization data to localStorage
    localStorage.setItem("brandName", brandName);
    localStorage.setItem("tagline", tagline);
    localStorage.setItem("colorTheme", colorTheme);
    if (logo) localStorage.setItem("logo", logo);
  }, [brandName, tagline, colorTheme, logo]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogo(reader.result);
    };
    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const toggleView = () => {
    if (isFullView) {
      history.push(`/services/website-building/${selectedTemplate}`); // Navigate back to normal view
    } else {
      history.push(`/services/website-building/${selectedTemplate}/fullview`); // Navigate to full view
    }
  };

  const handleTemplateChange = (e) => {
    const newSlug = e.target.value;
    setSelectedTemplate(newSlug);
    history.push(`/services/website-building/${newSlug}`); // Update the route to the new template
  };

  if (!template) return <div>Template not found!</div>;

  return isFullView ? (
    // Render only the TemplatePreviewWrapper in full view mode
    <div className="min-h-screen relative">
      <button
        onClick={toggleView}
        className="absolute top-24 left-4 px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-900 z-50"
      >
        Back to Customization
      </button>
      <TemplatePreviewWrapper
        componentName={template.component}
        brandName={brandName}
        tagline={tagline}
        logo={logo}
        colorTheme={colorTheme}
      />
    </div>
  ) : (
    // Render the full page with Layout
    <Layout>
      <div className="p-8 max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">
          Explore and Customize Your Website Template
        </h1>

        {/* Customization Form */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Template Selector */}
          <select
            value={selectedTemplate}
            onChange={handleTemplateChange}
            className="border p-3 rounded"
          >
            {templates.map((template) => (
              <option key={template.slug} value={template.slug}>
                {template.title}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Enter Brand Name"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            className="border p-3 rounded"
          />
          <input
            type="text"
            placeholder="Enter Tagline"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            className="border p-3 rounded"
          />
          {/* Logo upload */}
          <input
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            className="border p-3 rounded"
          />
          {/* Color Theme Selector */}
          <select
            value={colorTheme}
            onChange={(e) => setColorTheme(e.target.value)}
            className="border p-3 rounded"
          >
            <option value="blue">Blue</option>
            <option value="green">Green</option>
            <option value="purple">Purple</option>
            <option value="red">Red</option>
          </select>
        </div>
        <div className="mt-8">
          <h2 className="text-2xl font-semibold mb-4 text-center">
            Template Preview
          </h2>
          <div className="border border-gray-300 shadow-lg rounded-lg overflow-hidden bg-white">
            <TemplatePreviewWrapper
              componentName={template.component}
              brandName={brandName}
              tagline={tagline}
              logo={logo}
              colorTheme={colorTheme}
            />
          </div>
        </div>
        {/* Toggle Button */}
        <div className="text-center mt-6">
          <button
            onClick={toggleView}
            className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
          >
            View Full Template
          </button>
        </div>

        {/* Pricing and Contact */}
        <div className="text-center mt-12">
          <p className="text-lg mb-4">
            Starting from <span className="font-bold">{template.price}</span>
          </p>
          <a
            href="/contact-us"
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-lg"
          >
            Build this Website for Me 🚀
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default TemplateDetails;
